import { MutableRefObject, useEffect } from 'react';
import videojs from 'video.js';

interface Options {
    playerRef: MutableRefObject<ReturnType<typeof videojs> | null>;
    hasStartedRef: MutableRefObject<boolean>;
    isEndedRef: MutableRefObject<boolean>;
    isFocusedRef: MutableRefObject<boolean>;
    onToggleFullscreen: () => void;
    onClose?: () => void;
}

/**
 * Registers window-level keyboard controls for the video player.
 * Controls only activate when the video player container is focused.
 *
 * Focused + video active (started & not ended):
 *   Space play/pause, ←/→ seek ±5s (stops propagation), ↑/↓ volume, F fullscreen
 *
 * Focused + video not active:
 *   Space play/pause only — ←/→ are not intercepted so gallery navigation still works
 *
 * Not focused: no interception — gallery navigation handles ←/→ normally
 */
const useVideoKeyboard = ({
    playerRef,
    hasStartedRef,
    isEndedRef,
    isFocusedRef,
    onToggleFullscreen,
    onClose
}: Options): void => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;

            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement
            ) {
                return;
            }

            if (!isFocusedRef.current) {
                return;
            }

            const player = playerRef.current;

            if (!player) {
                return;
            }

            const videoActive = hasStartedRef.current && !isEndedRef.current;

            switch (e.key) {
                case ' ':
                    e.preventDefault();
                    e.stopPropagation();

                    if (player.paused()) {
                        player.play();
                    } else {
                        player.pause();
                    }

                    break;

                case 'ArrowLeft':
                    if (videoActive) {
                        e.preventDefault();
                        e.stopPropagation();
                        player.currentTime(Math.max(0, (player.currentTime() ?? 0) - 5));
                    }

                    break;

                case 'ArrowRight':
                    if (videoActive) {
                        e.preventDefault();
                        e.stopPropagation();
                        player.currentTime(
                            Math.min(player.duration() ?? 0, (player.currentTime() ?? 0) + 5)
                        );
                    }

                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    player.volume(Math.min(1, (player.volume() ?? 0) + 0.1));
                    break;

                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    player.volume(Math.max(0, (player.volume() ?? 0) - 0.1));
                    break;

                case 'f':
                case 'F':
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFullscreen();
                    break;

                case 'Escape':
                    e.preventDefault();
                    onClose?.();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [onClose, onToggleFullscreen, playerRef, hasStartedRef, isEndedRef, isFocusedRef]);
};

export default useVideoKeyboard;
